/**
 * The order the engine walks stations in, sorted once per kitchen rather than
 * on every tick.
 *
 * Anything that resolves per station walks the keys in codepoint order, so the
 * events for a given state are the same whatever order the stations were
 * inserted in. A round never adds or removes a station, so the sorted list is
 * computed for one `stations` object and handed on to each tick's copy by
 * `cloneState`. Any other object (a keyframe, a hand-built test state) misses
 * and sorts its own keys.
 */

import type { Station } from '@overcooked/shared'
import type { GameState } from './state'

const ORDER = new WeakMap<Record<string, Station>, readonly string[]>()

export function stationOrder(state: GameState): readonly string[] {
  const cached = ORDER.get(state.stations)
  if (cached) return cached
  const order = Object.keys(state.stations).sort()
  ORDER.set(state.stations, order)
  return order
}

/**
 * `next` is a copy of `prev` holding `count` stations. It reuses `prev`'s list
 * only when the counts agree, so a key added to `prev` after it was sorted
 * makes the copy sort again.
 */
export function inheritStationOrder(
  prev: Record<string, Station>,
  next: Record<string, Station>,
  count: number,
): void {
  const cached = ORDER.get(prev)
  if (cached && cached.length === count) ORDER.set(next, cached)
}
